/**
 * Reading a contact list out of a spreadsheet.
 *
 * This is unglamorous and worth doing properly. A list is the customer's own data, exported
 * from a CRM or typed by hand, and every failure here is silent: a quoted address containing
 * a comma splits into two columns, an Excel-mangled phone number sends nothing, a stray
 * header row messages someone called "Phone". None of that surfaces as an error — it
 * surfaces as a campaign that went to the wrong people, after the credits are spent.
 */

/**
 * Splits CSV text into rows.
 *
 * Follows RFC 4180 rather than splitting on commas: fields may be quoted, quoted fields may
 * contain commas and line breaks, and a doubled quote inside a quoted field is a literal
 * quote. Handles CRLF and a UTF-8 byte order mark, both of which Excel emits by default.
 */
export function parseCsv(text: string, delimiter = ','): string[][] {
  const input = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text

  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let quoted = false
  let started = false

  const endField = () => {
    row.push(field)
    field = ''
    started = false
  }
  const endRow = () => {
    endField()
    rows.push(row)
    row = []
  }

  for (let i = 0; i < input.length; i++) {
    const char = input[i]

    if (quoted) {
      if (char === '"') {
        if (input[i + 1] === '"') {
          field += '"'
          i++
        } else {
          quoted = false
        }
      } else {
        field += char
      }
      continue
    }

    if (char === '"' && !started) {
      quoted = true
      started = true
      continue
    }

    if (char === delimiter) {
      endField()
      continue
    }

    if (char === '\r') {
      // Swallow the LF of a CRLF pair rather than emitting a blank row.
      if (input[i + 1] === '\n') i++
      endRow()
      continue
    }

    if (char === '\n') {
      endRow()
      continue
    }

    field += char
    started = true
  }

  // A file that ends without a newline still has a final row worth keeping.
  if (field !== '' || row.length > 0) endRow()

  // Trailing blank lines are an artefact of the export, not empty contacts.
  return rows.filter((r) => r.some((cell) => cell.trim() !== ''))
}

/**
 * Works out the separator a file actually uses.
 *
 * Excel writes semicolons in locales where the comma is the decimal separator, and exporting
 * from those machines is common enough that assuming a comma would leave a whole region of
 * customers with a single unusable column.
 */
export function detectDelimiter(text: string): string {
  const sample = text.split(/\r?\n/).slice(0, 5).join('\n')
  const candidates = [',', ';', '\t', '|']

  let best = ','
  let bestScore = 0

  for (const candidate of candidates) {
    const rows = parseCsv(sample, candidate)
    if (rows.length === 0) continue

    // Judged on consistency rather than on the most columns. A comma inside a quoted address
    // splits one row and not the others, which looks like two columns if you only count the
    // widest row — but a real separator gives every row the same width.
    const widths = rows.map((r) => r.length)
    const frequency = new Map<number, number>()
    for (const width of widths) frequency.set(width, (frequency.get(width) ?? 0) + 1)

    let modal = 0
    let modalCount = 0
    for (const [width, count] of frequency) {
      if (count > modalCount || (count === modalCount && width > modal)) {
        modal = width
        modalCount = count
      }
    }

    if (modal < 2) continue
    const score = modal * (modalCount / widths.length)
    if (score > bestScore) {
      bestScore = score
      best = candidate
    }
  }

  return best
}

/* ---------------------------------------------------------------- phone numbers */

export interface PhoneResult {
  /** E.164, with the leading plus. Null when the value could not be trusted. */
  phone: string | null
  /** Why it was rejected or changed. Shown to the customer before anything is sent. */
  issue: string | null
}

/** WhatsApp identities are already resolved; a pasted JID is passed through untouched. */
const JID = /^[0-9]+@(s\.whatsapp\.net|g\.us|c\.us)$/

/**
 * Turns whatever a spreadsheet holds into a number WhatsApp will accept.
 *
 * `defaultDialCode` is the country to assume for a national-format number, e.g. '91'. Without
 * one, a bare ten-digit number is ambiguous — the same digits are a valid mobile in India and
 * in the United States — so it is rejected rather than guessed. Guessing here would send a
 * campaign to real strangers on the other side of the world.
 */
export function normalisePhone(raw: string, defaultDialCode?: string): PhoneResult {
  const trimmed = raw.trim()
  if (!trimmed) return { phone: null, issue: 'Empty.' }

  if (JID.test(trimmed)) return { phone: trimmed, issue: null }

  // Excel stores long numbers as floats and exports them in scientific notation, losing the
  // last digits for good. Recovering them is impossible, so this must be reported, not
  // silently dialled.
  if (/^[0-9]\.[0-9]+e\+?[0-9]+$/i.test(trimmed)) {
    return {
      phone: null,
      issue: 'The spreadsheet stored this as a number and lost digits. Format the column as text and export again.',
    }
  }

  // A leading apostrophe is Excel's "treat as text" marker and is not part of the number.
  const cleaned = trimmed.replace(/^'/, '')

  if (/[a-z]/i.test(cleaned)) return { phone: null, issue: 'Not a phone number.' }

  const hadPlus = cleaned.startsWith('+')
  let digits = cleaned.replace(/[^0-9]/g, '')

  if (!digits) return { phone: null, issue: 'No digits.' }

  // The international prefix dialled from a landline, not part of the number itself.
  if (!hadPlus && digits.startsWith('00')) {
    digits = digits.slice(2)
    return finish(digits)
  }

  if (hadPlus) return finish(digits)

  const dial = defaultDialCode?.replace(/[^0-9]/g, '')

  if (dial) {
    // Already carries the country code.
    if (digits.startsWith(dial) && digits.length > dial.length + 6) return finish(digits)
    // A national trunk prefix, dropped when the country code goes on.
    const national = digits.replace(/^0+/, '')
    return finish(dial + national)
  }

  if (digits.length >= 11) return finish(digits)

  return {
    phone: null,
    issue: 'No country code, and no default country chosen.',
  }
}

function finish(digits: string): PhoneResult {
  // No country code begins with zero, so a leading zero means this is still a national
  // number carrying its trunk prefix — recognisable only if the country is known. Accepting
  // it would produce a number that looks plausible and reaches nobody.
  if (digits.startsWith('0')) {
    return {
      phone: null,
      issue: 'This is a local number. Choose the country it belongs to, or add the country code.',
    }
  }
  // E.164 allows at most fifteen digits; a country code is at least one and no real
  // subscriber number is under six.
  if (digits.length < 8) return { phone: null, issue: 'Too short to be a phone number.' }
  if (digits.length > 15) return { phone: null, issue: 'Too long to be a phone number.' }
  return { phone: `+${digits}`, issue: null }
}

/** WhatsApp addresses individuals by JID; this is the form the session host sends to. */
export function toJid(phone: string): string {
  if (phone.includes('@')) return phone
  return `${phone.replace(/^\+/, '')}@s.whatsapp.net`
}

/**
 * The reverse, for joining an audience of JIDs back onto anything stored by phone number.
 *
 * A group has no phone number and returns `null` rather than something that would collide
 * with a real one — a group id is a long numeric string, so a lenient reading of it would
 * produce a plausible-looking number belonging to nobody.
 */
export function fromJid(remoteId: string): string | null {
  if (!remoteId || remoteId.endsWith('@g.us')) return null
  // Multi-device JIDs carry a device suffix that is not part of the number.
  const digits = remoteId.split('@')[0].split(':')[0].replace(/\D/g, '')
  if (digits.length < 8 || digits.length > 15) return null
  return `+${digits}`
}

/* ---------------------------------------------------------------- mapping columns */

const PHONE_HEADERS = [
  'phone', 'mobile', 'number', 'whatsapp', 'contact', 'msisdn', 'cell', 'telephone', 'tel',
]
const NAME_HEADERS = ['name', 'first name', 'firstname', 'full name', 'customer', 'contact name']

/** Looks like a phone number without committing to whether it is a valid one. */
function looksNumeric(value: string): boolean {
  const digits = value.replace(/[^0-9]/g, '')
  return digits.length >= 8 && digits.length <= 15 && !/[a-z]{2}/i.test(value)
}

export interface ColumnGuess {
  /** Index of the column holding the number, or -1 if none could be identified. */
  phoneIndex: number
  /** Index of a likely display name, or -1. */
  nameIndex: number
  /** Whether the first row is a header rather than a contact. */
  hasHeader: boolean
}

/**
 * Guesses which column holds the number.
 *
 * The header is consulted first, but only as a hint: exports label the column anything from
 * "Mobile" to "WA". The content is the real evidence, so a file with no header — or a
 * misleading one — still maps correctly.
 */
export function inferColumns(rows: string[][]): ColumnGuess {
  if (rows.length === 0) return { phoneIndex: -1, nameIndex: -1, hasHeader: false }

  const first = rows[0]
  const lower = first.map((c) => c.trim().toLowerCase())

  // A header row is one whose cells are labels rather than data — in particular, whose
  // "phone" column does not itself contain a phone number.
  const hasHeader =
    first.every((cell) => cell.trim() !== '' && !looksNumeric(cell)) &&
    lower.some((cell) => PHONE_HEADERS.some((h) => cell.includes(h)) || NAME_HEADERS.includes(cell))

  const body = hasHeader ? rows.slice(1) : rows
  const width = Math.max(...rows.map((r) => r.length))

  let phoneIndex = -1
  if (hasHeader) {
    phoneIndex = lower.findIndex((cell) => PHONE_HEADERS.some((h) => cell.includes(h)))
  }

  if (phoneIndex === -1) {
    // Whichever column most often looks like a number wins.
    let bestScore = 0
    for (let col = 0; col < width; col++) {
      const score = body.filter((r) => looksNumeric(r[col] ?? '')).length
      if (score > bestScore) {
        bestScore = score
        phoneIndex = col
      }
    }
    if (bestScore < Math.max(1, body.length / 2)) phoneIndex = -1
  }

  let nameIndex = -1
  if (hasHeader) {
    nameIndex = lower.findIndex((cell) => NAME_HEADERS.some((h) => cell === h || cell.includes(h)))
  }
  if (nameIndex === -1) {
    // The first non-numeric column that is not the phone column.
    for (let col = 0; col < width; col++) {
      if (col === phoneIndex) continue
      const nonNumeric = body.filter((r) => (r[col] ?? '').trim() && !looksNumeric(r[col] ?? '')).length
      if (nonNumeric >= Math.max(1, body.length / 2)) {
        nameIndex = col
        break
      }
    }
  }

  return { phoneIndex, nameIndex, hasHeader }
}

export interface ImportedRecipient {
  remoteId: string
  displayName?: string
  variables: Record<string, string>
}

export interface ImportResult {
  recipients: ImportedRecipient[]
  /** Rows that could not be used, with the reason and the original line number. */
  rejected: { line: number; value: string; issue: string }[]
  /** Contacts that appeared more than once; the first occurrence is kept. */
  duplicates: number
  /** Variable names available to the message body, taken from the column headers. */
  variables: string[]
}

/**
 * Turns parsed rows into recipients.
 *
 * Every column other than the number becomes a variable the message can use, named after its
 * header, so a list carrying an order reference or an appointment time can personalise
 * without a second import step. Rows that cannot be used are returned rather than dropped —
 * the customer needs to see which of their contacts will not be reached, and why.
 */
export function toRecipients(
  rows: string[][],
  options: { phoneIndex: number; nameIndex?: number; hasHeader: boolean; defaultDialCode?: string },
): ImportResult {
  const header = options.hasHeader ? rows[0] : []
  const body = options.hasHeader ? rows.slice(1) : rows

  const columnName = (index: number) => {
    const raw = header[index]?.trim()
    if (raw) return raw.toLowerCase().replace(/[^a-z0-9_ -]/g, '').trim() || `column${index + 1}`
    return `column${index + 1}`
  }

  const width = Math.max(0, ...rows.map((r) => r.length))
  const variables: string[] = []
  for (let col = 0; col < width; col++) {
    if (col === options.phoneIndex) continue
    variables.push(col === options.nameIndex ? 'name' : columnName(col))
  }

  const recipients: ImportedRecipient[] = []
  const rejected: ImportResult['rejected'] = []
  const seen = new Set<string>()
  let duplicates = 0

  body.forEach((row, index) => {
    // The line number the customer sees in their spreadsheet, header included.
    const line = index + 1 + (options.hasHeader ? 1 : 0)
    const raw = row[options.phoneIndex] ?? ''
    const { phone, issue } = normalisePhone(raw, options.defaultDialCode)

    if (!phone) {
      rejected.push({ line, value: raw.trim(), issue: issue ?? 'Could not be read.' })
      return
    }

    const remoteId = toJid(phone)
    if (seen.has(remoteId)) {
      duplicates++
      return
    }
    seen.add(remoteId)

    const values: Record<string, string> = {}
    let cursor = 0
    for (let col = 0; col < width; col++) {
      if (col === options.phoneIndex) continue
      const value = (row[col] ?? '').trim()
      if (value) values[variables[cursor]] = value
      cursor++
    }

    recipients.push({
      remoteId,
      displayName: options.nameIndex !== undefined && options.nameIndex >= 0
        ? (row[options.nameIndex] ?? '').trim() || undefined
        : undefined,
      variables: values,
    })
  })

  return { recipients, rejected, duplicates, variables }
}

/* ---------------------------------------------------------------- writing */

/**
 * Characters that make a spreadsheet treat a cell as a formula rather than as text.
 *
 * This is the part of exporting that bites. A customer messages `=cmd|'/c calc'!A1`, it is
 * stored faithfully, exported faithfully, and then executed the moment someone opens the file
 * in Excel. The message body is attacker-controlled text arriving from strangers, so every
 * cell is treated as hostile.
 */
const FORMULA_LEAD = ['=', '+', '-', '@', '\t', '\r']

/**
 * Escapes one cell.
 *
 * Quoting alone is not enough: `"=1+1"` is still a formula to Excel. A leading apostrophe is
 * the standard mitigation — spreadsheets read it as "this is text" and do not show it.
 */
export function escapeCsvCell(value: unknown): string {
  if (value === null || value === undefined) return ''

  let text =
    value instanceof Date
      ? value.toISOString()
      : typeof value === 'object'
        ? JSON.stringify(value)
        : String(value)

  if (FORMULA_LEAD.some((lead) => text.startsWith(lead))) text = `'${text}`

  // Quote whenever the cell contains something that would otherwise change its shape.
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`
  return text
}

export function toCsvRow(cells: unknown[]): string {
  return cells.map(escapeCsvCell).join(',')
}

/**
 * Builds a whole file.
 *
 * A byte order mark is prepended because Excel on Windows otherwise reads a UTF-8 file as the
 * local codepage, and every non-Latin name in the export arrives as mojibake. CRLF for the
 * same reason: it is what every spreadsheet expects.
 */
export function toCsv(headers: string[], rows: unknown[][]): string {
  return `\ufeff${[toCsvRow(headers), ...rows.map(toCsvRow)].join('\r\n')}\r\n`
}

/** A filename that is safe on every platform and says what the file is. */
export function exportFileName(kind: string, at: Date, extension: 'csv' | 'xls' = 'csv'): string {
  const stamp = at.toISOString().slice(0, 19).replace(/[:T]/g, '-')
  return `${kind.replace(/[^a-z0-9-]/gi, '')}-${stamp}.${extension}`
}

function escapeXml(value: unknown): string {
  const text =
    value === null || value === undefined
      ? ''
      : value instanceof Date
        ? value.toISOString()
        : typeof value === 'object'
          ? JSON.stringify(value)
          : String(value)
  return text.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' })[ch]!)
}

/**
 * A whole file in Excel's SpreadsheetML 2003 XML dialect — every cell is text, deliberately:
 * the CSV export already handles the "protect against a formula-injection cell" case, and
 * matching that behaviour exactly rather than adding a second, differently-typed code path is
 * worth more than native numeric columns in the one format nobody asked to be exact about.
 * Opens directly in Excel, Google Sheets, and LibreOffice with no ZIP container needed, which
 * a real `.xlsx` (OOXML) would require and this codebase has no library on hand to build.
 */
export function toSpreadsheetXml(sheetName: string, headers: string[], rows: unknown[][]): string {
  const row = (cells: unknown[]) =>
    `<Row>${cells.map((c) => `<Cell><Data ss:Type="String">${escapeXml(c)}</Data></Cell>`).join('')}</Row>`

  return [
    '<?xml version="1.0"?>',
    '<?mso-application progid="Excel.Sheet"?>',
    '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">',
    `<Worksheet ss:Name="${escapeXml(sheetName.slice(0, 31))}"><Table>`,
    row(headers),
    ...rows.map(row),
    '</Table></Worksheet>',
    '</Workbook>',
  ].join('')
}
